import React from 'react';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import Chip from '@mui/material/Chip';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import MDProgress from 'components/MDProgress';

function statusLabel(m) {
  if (m?.membershipStatus === 'FROZEN') return 'Donduruldu';
  if (m?.membershipStatus === 'PASSIVE') return 'Pasif';
  if (m?.membershipStatus === 'ACTIVE') return 'Aktif';
  if (typeof m?.active === 'boolean') return m.active ? 'Aktif' : 'Pasif';
  return '—';
}

function debtTry(m) {
  if (typeof m?.debtTry === 'number') return Math.max(0, m.debtTry);
  if (typeof m?.balanceCents === 'number') return Math.max(0, Math.round(m.balanceCents / 100));
  return 0;
}

function dateShort(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  return `${dd}.${mm}.${yy}`;
}

function DebtChip({ value, disabled }) {
  const v = Number(value || 0);
  const ok = v <= 0;

  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<Icon sx={{ fontSize: 16 }}>{ok ? 'check_circle' : 'priority_high'}</Icon>}
      label={ok ? '0 ₺' : `${v} ₺`}
      sx={{
        borderRadius: '999px',
        fontWeight: 800,
        borderColor: ok ? 'rgba(76,175,80,.35)' : 'rgba(244,67,54,.35)',
        background: ok ? 'rgba(76,175,80,.06)' : 'rgba(244,67,54,.06)',
        ...(disabled ? { opacity: 0.55, filter: 'grayscale(0.25)' } : null),
      }}
    />
  );
}

function StatusChip({ label, disabled }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      sx={{
        borderRadius: '999px',
        fontWeight: 800,
        ...(disabled ? { opacity: 0.55, filter: 'grayscale(0.25)' } : null),
      }}
    />
  );
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function percentUsed(total, used) {
  const t = Number(total ?? 0);
  const u = Number(used ?? 0);
  if (!t || t <= 0) return 0;
  return Math.round((u / t) * 100);
}

function usageTone(p) {
  if (p >= 100) return 'error';
  if (p >= 80) return 'warning';
  return 'info';
}

function UsageProgressCell({ totalSessions, usedSessions, totalMakeUp, usedMakeUp, disabled }) {
  const p = percentUsed(totalSessions, usedSessions);
  const value = clamp(p, 0, 100);
  const color = usageTone(value);

  return (
    <Tooltip
      placement="top"
      arrow
      title={
        <MDBox sx={{ p: 0.5 }}>
          <MDTypography variant="caption" fontWeight="bold" sx={{ color: '#fff' }}>
            Normal hak: {usedSessions ?? 0}/{totalSessions ?? 0}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: '#fff', display: 'block', mt: 0.3 }}>
            Telafi: {usedMakeUp ?? 0}/{totalMakeUp ?? 0}
          </MDTypography>
        </MDBox>
      }
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0,0,0,.92)',
            borderRadius: '10px',
            boxShadow: '0 12px 30px rgba(0,0,0,.25)',
            maxWidth: 240,
            whiteSpace: 'pre-line',
          },
        },
        arrow: { sx: { color: 'rgba(0,0,0,.92)' } },
      }}
    >
      <MDBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <MDTypography variant="caption" fontWeight="bold" sx={{ minWidth: 36 }}>
          {value}%
        </MDTypography>

        <MDBox sx={{ width: '9rem' }}>
          <MDProgress variant="gradient" color={color} value={value} />
        </MDBox>
      </MDBox>
    </Tooltip>
  );
}

function CellWrap({ disabled, children }) {
  const theme = useMuiTheme();
  if (!disabled) return children;

  return (
    <MDBox
      sx={{
        px: 0.75,
        py: 0.45,
        borderRadius: '12px',
        background: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
        opacity: 0.75,
      }}
    >
      {children}
    </MDBox>
  );
}
export default function membersTableData({ items, onOpenMenu, onOpenDetail }) {
  return {
    columns: [
      { Header: 'Üye', accessor: 'member', sort: true },
      { Header: 'Telefon', accessor: 'phone', sort: true },
      { Header: 'Paket', accessor: 'plan', sort: true },

      {
        Header: 'Kullanım',
        accessor: 'usage', // ✅ UI basılacak
        sort: true,
        align: 'center',
        sortType: (rowA, rowB) => (rowA.original.usageSort ?? 0) - (rowB.original.usageSort ?? 0),
      },
      {
        Header: 'Borç',
        accessor: 'debt', // ✅ UI basılacak
        sort: true,
        align: 'center',
        sortType: (rowA, rowB) => (rowA.original.debtSort ?? 0) - (rowB.original.debtSort ?? 0),
      },

      { Header: 'Kayıt', accessor: 'createdAt', sort: true, align: 'center' },
      { Header: 'Durum', accessor: 'status', sort: true, align: 'center' },
      { Header: '', accessor: 'actions', sort: false, align: 'right' },
    ],

    rows: (items || []).map((m) => {
      const name =
        m?.fullName || `${m?.name || ''} ${m?.surname || ''}`.trim() || `#${m?.id ?? ''}`;
      const planName = m?.planName || m?.activePlanName || '—';

      const isPassive =
        String(m?.membershipStatus || '').toUpperCase() === 'PASSIVE' || m?.active === false;

      const textMuted = isPassive ? { opacity: 0.6, color: 'text.secondary' } : undefined;
      const textBold = isPassive ? { opacity: 0.6 } : undefined;

      const usageSort = clamp(percentUsed(m?.totalSessions, m?.usedSessions), 0, 100);
      const debtSort = debtTry(m);

      return {
        usageSort, // ✅ gizli sort alanı
        debtSort, // ✅ gizli sort alanı

        member: (
          <CellWrap disabled={isPassive}>
            <MDBox onClick={() => onOpenDetail?.(m)} sx={{ cursor: 'pointer' }}>
              <MDTypography variant="button" fontWeight="bold" sx={textBold}>
                {name}
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ color: 'text.secondary', ...(isPassive ? { opacity: 0.6 } : null) }}
              >
                {m?.trainerName ? `Eğitmen: ${m.trainerName}` : ' '}
              </MDTypography>
            </MDBox>
          </CellWrap>
        ),

        usage: (
          <CellWrap disabled={isPassive}>
            <UsageProgressCell
              totalSessions={m?.totalSessions}
              usedSessions={m?.usedSessions}
              totalMakeUp={m?.totalMakeUp}
              usedMakeUp={m?.usedMakeUp}
              disabled={isPassive}
            />
          </CellWrap>
        ),

        debt: (
          <CellWrap disabled={isPassive}>
            <DebtChip value={debtSort} disabled={isPassive} />
          </CellWrap>
        ),

        createdAt: (
          <CellWrap disabled={isPassive}>
            <MDTypography variant="button" sx={textMuted}>
              {dateShort(m?.createdAt)}
            </MDTypography>
          </CellWrap>
        ),

        status: (
          <CellWrap disabled={isPassive}>
            <StatusChip label={statusLabel(m)} disabled={isPassive} />
          </CellWrap>
        ),

        phone: (
          <CellWrap disabled={isPassive}>
            <MDTypography variant="button" sx={textMuted}>
              {m?.phone || '—'}
            </MDTypography>
          </CellWrap>
        ),

        plan: (
          <CellWrap disabled={isPassive}>
            <MDBox display="flex" alignItems="center" gap={0.6}>
              <Icon sx={{ opacity: isPassive ? 0.35 : 0.75, fontSize: 18 }}>redeem</Icon>
              <MDTypography variant="button" fontWeight="bold" sx={textBold}>
                {planName}
              </MDTypography>
            </MDBox>
          </CellWrap>
        ),

        actions: (
          <CellWrap disabled={isPassive}>
            <MDButton
              variant="text"
              color="dark"
              onClick={(e) => {
                e.stopPropagation();
                onOpenMenu?.(e, m);
              }}
              sx={{
                minWidth: 40,
                width: 40,
                height: 36,
                borderRadius: '12px',
                opacity: isPassive ? 0.6 : 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>more_vert</Icon>
            </MDButton>
          </CellWrap>
        ),
      };
    }),
  };
}
